import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Bot,
  Building2,
  BellRing,
  CalendarRange,
  Clock3,
  Megaphone,
  Network,
  Stethoscope,
} from 'lucide-react'

import { Spinner } from '../../ui/Spinner/Spinner'
import { Button } from '../../ui/Button/Button'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenant/TenantProvider'
import { AppointmentsSettings } from './AppointmentsSettings'
import { AssistantSettings } from './AssistantSettings'
import { ClinicProfileSettings } from './ClinicProfileSettings'
import { NotificationSettings } from './NotificationSettings'
import { CampaignSettings } from './CampaignSettings'
import { WorkingHoursSettings } from './WorkingHoursSettings'
import { OrganizationSettings } from './OrganizationSettings'
import { ServicesSettings } from './ServicesSettings'
import { useOrganizationAdmin } from '../onboarding/useOrganizationAdmin'
import { getOnboardingApiClient } from '../onboarding/onboarding-api'
import { prefetchOrganizationAccess } from './organization-queries'
import type { OrganizationResponse } from '../onboarding/organization-onboarding-contracts'
import {
  createNotificationSettingsRepository,
  type NotificationSettingsRepository,
} from './notification-settings-repository'
import {
  createOperatoryPolicyRepository,
  type OperatoryPolicyRepository,
} from './operatory-policy-repository'
import {
  createSettingsRepository,
  type SettingsRepository,
} from './settings-repository'
import {
  createProviderResourceRepository,
  type ProviderResourceRepository,
} from './provider-resource-repository'
import styles from './SettingsPage.module.css'
import { SettingsConfirmDialog } from './SettingsConfirmDialog'
import { SettingsUnsavedChanges } from './settings-unsaved-changes'
import {
  canManageOrganization,
  canViewClinicTechnicalDetails,
} from './settings-access'
import {
  createAssistantSettingsRepository,
  type AssistantSettingsRepository,
} from './assistant-settings-repository'
import {
  createCampaignSettingsRepository,
  type CampaignSettingsRepository,
} from './campaign-settings-repository'

export type SettingsSection =
  | 'clinic'
  | 'hours'
  | 'services'
  | 'appointments'
  | 'assistant'
  | 'campaigns'
  | 'notifications'
  | 'organization'

const SECTIONS: Array<{
  id: SettingsSection
  label: string
  icon: typeof Building2
}> = [
  { id: 'clinic', label: 'Clinic Info', icon: Building2 },
  { id: 'services', label: 'Services', icon: Stethoscope },
  { id: 'hours', label: 'Working Hours', icon: Clock3 },
  { id: 'appointments', label: 'Appointments', icon: CalendarRange },
  { id: 'assistant', label: 'AI & Rules', icon: Bot },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'notifications', label: 'Notifications', icon: BellRing },
  { id: 'organization', label: 'Organization', icon: Network },
]

const SECTION_NAMES: Record<SettingsSection, string> = {
  clinic: 'Clinic Info',
  services: 'Services',
  hours: 'Working Hours',
  appointments: 'Appointments',
  assistant: 'AI & Rules',
  campaigns: 'Campaigns',
  notifications: 'Notifications',
  organization: 'Organization',
}

export function isSettingsSection(
  value: string | null
): value is SettingsSection {
  return SECTIONS.some((section) => section.id === value)
}

export function SettingsContent({
  tenantId,
  tenantLabel,
  repository,
  operatoryRepository,
  providerResourceRepository,
  notificationRepository,
  assistantSettingsRepository,
  campaignSettingsRepository,
  organization,
  organizationSectionRequested = false,
  prerequisiteSection,
  onContinueCommunications,
  initialSection = 'clinic',
  onSectionChange,
  onOrganizationIntent,
}: {
  tenantId: string
  tenantLabel: string
  repository: SettingsRepository
  operatoryRepository: OperatoryPolicyRepository
  providerResourceRepository?: ProviderResourceRepository
  notificationRepository?: NotificationSettingsRepository
  assistantSettingsRepository?: AssistantSettingsRepository
  campaignSettingsRepository?: CampaignSettingsRepository
  organization?: OrganizationResponse
  organizationSectionRequested?: boolean
  prerequisiteSection?: 'clinic' | 'hours' | 'services'
  onContinueCommunications?: () => void
  /** The section to open first, e.g. from a `?section=` link. Organization
   * opens only for an Owner or Admin. */
  initialSection?: SettingsSection
  /** Told about every section change, so the address can follow it. */
  onSectionChange?: (section: SettingsSection) => void
  /** An Owner or Admin is about to open Organization (hover or focus). */
  onOrganizationIntent?: () => void
}) {
  const [activeSection, setActiveSectionState] = useState<SettingsSection>(
    () =>
      initialSection === 'organization' && !canManageOrganization(organization)
        ? 'clinic'
        : initialSection
  )
  // A section switch waiting on "discard unsaved changes?".
  const [pendingSection, setPendingSection] = useState<SettingsSection | null>(
    null
  )
  const [dirtySections, setDirtySections] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const reportUnsaved = useCallback((section: string, dirty: boolean) => {
    setDirtySections((current) => {
      if (current.has(section) === dirty) return current
      const next = new Set(current)
      if (dirty) next.add(section)
      else next.delete(section)
      return next
    })
  }, [])
  // A section reports as `<section>` or, for several editors, `<section>:<part>`.
  const activeDirty = [...dirtySections].some(
    (key) => key === activeSection || key.startsWith(`${activeSection}:`)
  )
  const tabRefs = useRef(new Map<SettingsSection, HTMLButtonElement>())
  const sectionChange = useRef(onSectionChange)
  useEffect(() => {
    sectionChange.current = onSectionChange
  })

  const setActiveSection = useCallback((section: SettingsSection) => {
    setActiveSectionState(section)
    sectionChange.current?.(section)
  }, [])

  // Every user-chosen switch goes through here: a section with unsaved edits
  // asks before it is closed, because closing it drops them.
  const selectSection = useCallback(
    (section: SettingsSection) => {
      if (section === activeSection) return
      if (activeDirty) {
        setPendingSection(section)
        return
      }
      setActiveSection(section)
    },
    [activeDirty, activeSection, setActiveSection]
  )

  useEffect(() => {
    if (dirtySections.size === 0) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirtySections])

  // Owners and Admins only, from the server-verified organization answer: a
  // manager or staff member gets no tab and a `?section=organization` link
  // opens their default section instead.
  const organizationAccess = canManageOrganization(organization)
  // Lost access (or an answer still loading) never leaves its panel open.
  const shownSection =
    activeSection === 'organization' && !organizationAccess
      ? 'clinic'
      : activeSection

  useEffect(() => {
    if (organizationSectionRequested && canManageOrganization(organization)) {
      selectSection('organization')
    }
    // Only a new request or a newly loaded organization opens it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, organizationSectionRequested])

  useEffect(() => {
    if (prerequisiteSection) setActiveSection(prerequisiteSection)
  }, [prerequisiteSection, setActiveSection])

  useEffect(() => {
    tabRefs.current
      .get(activeSection)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [activeSection])

  // NexHealth technical details in Working Hours and Appointments: Owners and
  // Admins of this clinic only, from the server-verified organization answer.
  const technicalDetails = canViewClinicTechnicalDetails(organization, tenantId)

  const tabs = SECTIONS.filter(
    (section) => section.id !== 'organization' || organizationAccess
  )
  // Intent to open Organization starts its people reads early.
  const prefetchOrganization = () => {
    if (organizationAccess) onOrganizationIntent?.()
  }
  // Arrow keys move between tabs; Enter or Space opens one. Opening is manual
  // so moving past a section with unsaved edits never asks by surprise.
  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = tabs.findIndex(
      (tab) => tabRefs.current.get(tab.id) === event.currentTarget
    )
    const target =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + tabs.length) % tabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? tabs.length - 1
              : null
    if (target === null) return
    event.preventDefault()
    tabRefs.current.get(tabs[target]!.id)?.focus()
  }

  return (
    <section className={styles.page}>
      {onContinueCommunications ? (
        <section
          className={styles.communicationsContinuation}
          aria-label="Phone and texting setup continuation"
        >
          <div>
            <h2>Finish phone &amp; texting setup</h2>
            <p>
              Save Clinic Info and Working Hours for {tenantLabel}. For Voice,
              also add Services and mark at least one Available for Flio
              booking. Then continue; we’ll check the saved prerequisites before
              number setup.
            </p>
          </div>
          <div className={styles.organizationRowActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => selectSection('clinic')}
            >
              Clinic Info
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => selectSection('hours')}
            >
              Working Hours
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => selectSection('services')}
            >
              Services &amp; booking
            </Button>
            <Button type="button" onClick={onContinueCommunications}>
              Continue to phone &amp; texting
            </Button>
          </div>
        </section>
      ) : null}
      <div
        className={styles.settingsTabs}
        aria-label="Settings sections"
        role="tablist"
      >
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            ref={(node) => {
              if (node) tabRefs.current.set(id, node)
              else tabRefs.current.delete(id)
            }}
            id={`settings-tab-${id}`}
            type="button"
            role="tab"
            aria-selected={shownSection === id}
            aria-controls="settings-section-panel"
            tabIndex={shownSection === id ? 0 : -1}
            className={shownSection === id ? styles.activeTab : undefined}
            onClick={() => selectSection(id)}
            onKeyDown={onTabKeyDown}
            onPointerEnter={
              id === 'organization' ? prefetchOrganization : undefined
            }
            onFocus={id === 'organization' ? prefetchOrganization : undefined}
          >
            <Icon size={17} aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      <SettingsUnsavedChanges.Provider value={reportUnsaved}>
        <div
          id="settings-section-panel"
          role="tabpanel"
          aria-labelledby={`settings-tab-${shownSection}`}
          className={styles.sectionPanel}
        >
          {shownSection === 'clinic' ? (
            <ClinicProfileSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={repository}
            />
          ) : shownSection === 'hours' ? (
            <WorkingHoursSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={repository}
              providerResourceRepository={providerResourceRepository}
              showSchedulingContext={technicalDetails}
            />
          ) : shownSection === 'services' && assistantSettingsRepository ? (
            <ServicesSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              assistantRepository={assistantSettingsRepository}
              providerResourceRepository={providerResourceRepository}
            />
          ) : shownSection === 'appointments' ? (
            <AppointmentsSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={operatoryRepository}
              providerResourceRepository={providerResourceRepository}
              showPmsResources={technicalDetails}
            />
          ) : shownSection === 'assistant' && assistantSettingsRepository ? (
            <AssistantSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={assistantSettingsRepository}
            />
          ) : shownSection === 'campaigns' && campaignSettingsRepository ? (
            <CampaignSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={campaignSettingsRepository}
            />
          ) : shownSection === 'notifications' && notificationRepository ? (
            <NotificationSettings
              key={tenantId}
              tenantId={tenantId}
              tenantLabel={tenantLabel}
              repository={notificationRepository}
            />
          ) : shownSection === 'organization' &&
            canManageOrganization(organization) ? (
            <OrganizationSettings
              organization={organization}
              onOpenClinicSettings={() => selectSection('clinic')}
            />
          ) : null}
        </div>
      </SettingsUnsavedChanges.Provider>

      {pendingSection ? (
        <SettingsConfirmDialog
          title="Discard unsaved changes?"
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          onConfirm={() => {
            const next = pendingSection
            setPendingSection(null)
            setDirtySections((current) => {
              const left = [...current].filter(
                (key) =>
                  key !== activeSection && !key.startsWith(`${activeSection}:`)
              )
              return left.length === current.size ? current : new Set(left)
            })
            setActiveSection(next)
          }}
          onCancel={() => setPendingSection(null)}
        >
          <p>
            Your changes to {SECTION_NAMES[activeSection]} haven’t been saved.
            Opening {SECTION_NAMES[pendingSection]} discards them.
          </p>
        </SettingsConfirmDialog>
      ) : null}
    </section>
  )
}

export function SettingsPage({
  repository,
  operatoryRepository,
  providerResourceRepository,
  notificationRepository,
  assistantSettingsRepository,
  campaignSettingsRepository,
}: {
  repository?: SettingsRepository
  operatoryRepository?: OperatoryPolicyRepository
  providerResourceRepository?: ProviderResourceRepository
  notificationRepository?: NotificationSettingsRepository
  assistantSettingsRepository?: AssistantSettingsRepository
  campaignSettingsRepository?: CampaignSettingsRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const queryClient = useQueryClient()
  const organization = useOrganizationAdmin()
  const location = useLocation()
  const navigate = useNavigate()
  const search = new URLSearchParams(location.search)
  const setupTenantUid = search.get('communicationsSetup')
  const requestedSection = search.get('section')
  // The setup flow owns its own section choice (prerequisiteSection).
  const initialSection =
    setupTenantUid === null &&
    isSettingsSection(requestedSection) &&
    requestedSection !== 'organization'
      ? requestedSection
      : undefined
  const setupClinic =
    setupTenantUid &&
    ['OWNER', 'ADMIN'].includes(organization.data?.role ?? '') &&
    organization.data?.clinics.some(
      (clinic) => clinic.tenantUid === setupTenantUid
    )
      ? tenant.authorizedClinics.find((clinic) => clinic.id === setupTenantUid)
      : undefined
  // The URL carries only navigation context. Both canonical organization access
  // and reciprocal clinic membership must authorize its target before rendering Settings.
  useEffect(() => {
    if (setupClinic && tenant.selectedClinic?.id !== setupClinic.id) {
      tenant.selectClinic(setupClinic.id)
    }
  }, [setupClinic, tenant])
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const activeSettingsRepository = useMemo(
    () =>
      repository ?? createSettingsRepository({ getUser: () => currentUser }),
    [currentUser, repository]
  )
  const activeOperatoryRepository = useMemo(
    () =>
      operatoryRepository ??
      createOperatoryPolicyRepository({ getUser: () => currentUser }),
    [currentUser, operatoryRepository]
  )
  const activeNotificationRepository = useMemo(
    () =>
      notificationRepository ??
      createNotificationSettingsRepository({ getUser: () => currentUser }),
    [currentUser, notificationRepository]
  )
  const activeProviderResourceRepository = useMemo(
    () =>
      providerResourceRepository ??
      createProviderResourceRepository({ getUser: () => currentUser }),
    [currentUser, providerResourceRepository]
  )
  const activeAssistantSettingsRepository = useMemo(
    () =>
      assistantSettingsRepository ??
      createAssistantSettingsRepository({ getUser: () => currentUser }),
    [assistantSettingsRepository, currentUser]
  )
  const activeCampaignSettingsRepository = useMemo(
    () =>
      campaignSettingsRepository ??
      createCampaignSettingsRepository({ getUser: () => currentUser }),
    [campaignSettingsRepository, currentUser]
  )
  if (setupTenantUid !== null) {
    if (
      organization.isPending ||
      tenant.status === 'loading' ||
      tenant.status === 'idle'
    ) {
      return <Spinner label="Checking setup clinic access" />
    }
    if (!setupClinic) {
      return (
        <StatePanel
          title="Setup clinic unavailable"
          description="Return to Organization and choose a clinic you are authorized to manage."
        />
      )
    }
    if (tenant.selectedClinic?.id !== setupClinic.id) {
      return <Spinner label="Opening setup clinic settings" />
    }
  }
  if (
    tenant.status === 'ready' &&
    tenant.authorizedClinics.length === 0 &&
    canManageOrganization(organization.data) &&
    organization.data.clinics.length === 0
  ) {
    return <OrganizationSettings organization={organization.data} />
  }
  if (!tenant.selectedClinic) {
    return <Spinner label="Loading clinic settings" />
  }
  // A `?section=organization` link waits for the organization answer, so an
  // Owner or Admin lands on it directly and nobody else ever sees the tab.
  const organizationRequested =
    setupTenantUid === null && requestedSection === 'organization'
  if (organizationRequested && organization.isPending) {
    return <Spinner label="Loading settings" />
  }

  return (
    <SettingsContent
      tenantId={tenant.selectedClinic.id}
      tenantLabel={tenant.selectedClinic.name}
      repository={activeSettingsRepository}
      operatoryRepository={activeOperatoryRepository}
      providerResourceRepository={activeProviderResourceRepository}
      notificationRepository={activeNotificationRepository}
      assistantSettingsRepository={activeAssistantSettingsRepository}
      campaignSettingsRepository={activeCampaignSettingsRepository}
      organization={organization.data}
      organizationSectionRequested={requestedSection === 'organization'}
      onOrganizationIntent={() => {
        if (
          auth.status !== 'authenticated' ||
          !canManageOrganization(organization.data)
        )
          return
        try {
          prefetchOrganizationAccess(
            queryClient,
            getOnboardingApiClient(),
            auth.user,
            organization.data
          )
        } catch {
          // Prefetch is only a head start; the tab loads its own data.
        }
      }}
      initialSection={
        organizationRequested && canManageOrganization(organization.data)
          ? 'organization'
          : initialSection
      }
      onSectionChange={
        setupTenantUid === null
          ? (section) => {
              // The address follows the open section (replace, so Back still
              // leaves Settings), and a reload or shared link reopens it.
              const next = new URLSearchParams(location.search)
              next.set('section', section)
              void navigate(
                { pathname: location.pathname, search: `?${next.toString()}` },
                { replace: true }
              )
            }
          : undefined
      }
      prerequisiteSection={
        setupClinic
          ? search.get('section') === 'hours'
            ? 'hours'
            : search.get('section') === 'services'
              ? 'services'
              : 'clinic'
          : undefined
      }
      onContinueCommunications={
        setupClinic
          ? () =>
              void navigate(
                `/organization/clinics/${encodeURIComponent(setupClinic.id)}/communications`
              )
          : undefined
      }
    />
  )
}
